import { fetchDataThunk, makeFetchSlice } from 'app/redux-fetch'
import { createAsyncThunk } from '@reduxjs/toolkit'

import {
  claimDevice,
  claimDeviceBody,
  createOrGetNetworkHome,
  createOrGetNetworkHomeBody,
  fetchOrgAdmins,
  inviteOrgAdmins,
  deleteDomainAdmins,
  createWLAN,
  createSiteWLAN,
  updateWLAN,
  enableWLAN,
  deleteWLAN,
  deleteClaimDevice,
  fetchClaimDevices,
  fetchInventory,
  fetchSiteInfo,
  fetchSiteWlans,
  fetchSiteClients,
  fetchOrgTemplates,
  createOrgTemplate,
  deleteOrgTemplate,
  fetchOrgSiteWlans,
  fetchOrgSiteRFTemplate,
  updateOrgRFTemplate,
  fetchSiteDeviceStats,
  fetchSitePSKs,
  createSitePSKs,
  updateSitePSKs,
  updateSiteMultiPSKs,
  deleteSitePSKs,
  deleteSsidSitePSKs,
  optimizeRadios,
} from './api'
import { AppDispatch } from 'app/store'
import {
  CLAIM_DEVICES_REDUCER_NAME,
  CREATE_WLAN_REDUCER_NAME,
  INVENTORY_REDUCER_NAME,
  MIST_SITE_DATA_REDUCER_NAME,
  SITE_INFO_REDUCER_NAME,
  SITE_WLANS_REDUCER_NAME,
  SITE_CLIENTS_REDUCER_NAME,
  ORG_TEMPLATES_REDUCER_NAME,
  ORG_SITE_WLANS_REDUCER_NAME,
  ORG_RF_TEMPLATES_REDUCER_NAME,
  SITE_DEVICE_STATS_REDUCER_NAME,
  SITE_PSKS_REDUCER_NAME,
  ORG_ADMINS_REDUCER_NAME,
} from './consts'
import { MIST_ORGANIZATION_REDUCER_NAME } from 'features/domains/consts'
import {
  fetchMistOrganization,
  FetchMistOrganizationBody,
} from 'features/domains/api'
import {
  CreateWLANBody,
  UpdateWLANBody,
  EnableWLANBody,
  OrgTemplates,
  UpdateOrgRFTemplate,
  SiteWlan,
  SitePSKs,
  UpdateSitePSKs,
  OrgAdminInvite,
  RemoveAdmin,
  DeleteOrgTemplate,
  OptimizeRadios,
} from './types'

const mistOrganizationSlice = makeFetchSlice(MIST_ORGANIZATION_REDUCER_NAME)

export const mistOrganizationReducer = mistOrganizationSlice.reducer
export const mistOrganizationActions = mistOrganizationSlice.actions

interface FetchMistOrganizationThunkProps {
  rootId: string
  body: FetchMistOrganizationBody
}

export const fetchMistOrganizationThunk = createAsyncThunk(
  'domains/fetchMistOrganization',
  async ({ rootId, body }: FetchMistOrganizationThunkProps, { dispatch }) => {
    try {
      return await fetchDataThunk({
        dispatch,
        actions: mistOrganizationActions,
        asyncFunc: () => fetchMistOrganization(rootId, body),
      })
    } catch (error) {
      console.log('Error fetching Mist Organization', error)
      return ''
    }
  }
)

export const createOrGetNetworkHomeSlice = makeFetchSlice(
  MIST_SITE_DATA_REDUCER_NAME
)
export const createOrGetNetworkHomeReducer = createOrGetNetworkHomeSlice.reducer
export const createOrGetNetworkHomeActions = createOrGetNetworkHomeSlice.actions

export const createOrGetNetworkHomeThunk = (
  orgId: string,
  body: createOrGetNetworkHomeBody
) => {
  return async (dispatch: AppDispatch) => {
    return fetchDataThunk({
      dispatch,
      actions: createOrGetNetworkHomeActions,
      asyncFunc: () => createOrGetNetworkHome(orgId, body),
    })
  }
}

export const claimDeviceThunk = (
  orgId: string,
  siteId: string,
  body: claimDeviceBody
) => {
  return async (dispatch: AppDispatch) => {
    return fetchDataThunk({
      dispatch,
      asyncFunc: () => claimDevice(orgId, siteId, body),
    })
  }
}

const orgAdminsSlice = makeFetchSlice(ORG_ADMINS_REDUCER_NAME)

export const orgAdminsReducer = orgAdminsSlice.reducer
export const orgAdminsActions = orgAdminsSlice.actions

export const fetchOrgAdminsThunk = (orgId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: orgAdminsActions,
        asyncFunc: () => fetchOrgAdmins(orgId),
      })
    } catch (error) {
      console.log('Error fetching Org Admin', error)
    }
  }
}

export const inviteOrgAdminsThunk = (orgId: string, body: OrgAdminInvite) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        asyncFunc: () => inviteOrgAdmins(orgId, body),
      })
      await dispatch(fetchOrgAdminsThunk(orgId))
    } catch (error) {
      console.log('Error inviting Org Admin', error)
    }
  }
}

export const deleteDomainAdminsThunk = (
  domainId: string,
  body: RemoveAdmin
) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        asyncFunc: () => deleteDomainAdmins(domainId, body),
      })
    } catch (error) {
      console.log('Error deleting Domain Admin', error)
    }
  }
}

const siteInfoSlice = makeFetchSlice(SITE_INFO_REDUCER_NAME)

export const siteInfoReducer = siteInfoSlice.reducer
export const siteInfoActions = siteInfoSlice.actions

export const fetchSiteInfoThunk = (siteId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: siteInfoActions,
        asyncFunc: () => fetchSiteInfo(siteId),
      })
    } catch (error) {
      console.log('Error fetching Site Info', error)
    }
  }
}

const claimDevicesSlice = makeFetchSlice(CLAIM_DEVICES_REDUCER_NAME)

export const claimDevicesReducer = claimDevicesSlice.reducer
export const claimDevicesActions = claimDevicesSlice.actions

export const fetchClaimDevicesThunk = (siteId: string) => {
  return async (dispatch: AppDispatch) => {
    if (siteId) {
      try {
        await fetchDataThunk({
          dispatch,
          actions: claimDevicesActions,
          asyncFunc: () => fetchClaimDevices(siteId),
        })
      } catch (error) {
        console.log('Error fetching Claim Devices', error)
      }
    }
  }
}

const siteClientsSlice = makeFetchSlice(SITE_CLIENTS_REDUCER_NAME)

export const siteClientsReducer = siteClientsSlice.reducer
export const siteClientsActions = siteClientsSlice.actions

export const fetchSiteClientsThunk = (siteId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: siteClientsActions,
        asyncFunc: () => fetchSiteClients(siteId),
      })
    } catch (error) {
      console.log('Error fetching Site Clients', error)
    }
  }
}

const siteDeviceStatsSlice = makeFetchSlice(SITE_DEVICE_STATS_REDUCER_NAME)

export const siteDeviceStatsReducer = siteDeviceStatsSlice.reducer
export const siteDeviceStatsActions = siteDeviceStatsSlice.actions

export const fetchSiteDeviceStatsThunk = (siteId: string) => {
  return async (dispatch: AppDispatch) => {
    if (siteId) {
      try {
        await fetchDataThunk({
          dispatch,
          actions: siteDeviceStatsActions,
          asyncFunc: () => fetchSiteDeviceStats(siteId),
        })
      } catch (error) {
        console.log('Error fetching Site Device Stats', error)
      }
    }
  }
}

const siteWlansSlice = makeFetchSlice(SITE_WLANS_REDUCER_NAME)

export const siteWlansReducer = siteWlansSlice.reducer
export const siteWlansActions = siteWlansSlice.actions

export const fetchSiteWlansThunk = (siteId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: siteWlansActions,
        asyncFunc: () => fetchSiteWlans(siteId),
      })
    } catch (error) {
      console.log('Error fetching Site Wlans', error)
    }
  }
}

const createWLANSlice = makeFetchSlice(CREATE_WLAN_REDUCER_NAME)

export const createWLANReducer = createWLANSlice.reducer
export const createWLANActions = createWLANSlice.actions

export const createWLANThunk = (siteId: string, body: CreateWLANBody) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: createWLANActions,
        asyncFunc: () => createWLAN(siteId, body),
      })

      //await dispatch(fetchSiteWlansThunk(siteId))
    } catch (error) {
      console.log('Error while creating WLAN', error)
    }
  }
}

export const createSiteWlanThunk = (siteId: string, body: SiteWlan) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: createWLANActions,
        asyncFunc: () => createSiteWLAN(siteId, body),
      })

      await dispatch(fetchSiteWlansThunk(siteId))
    } catch (error) {
      console.log('Error while creating WLAN', error)
    }
  }
}

export const updateWLANThunk = (siteId: string, body: UpdateWLANBody) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: createWLANActions,
        asyncFunc: () => updateWLAN(siteId, body),
      })

      //await dispatch(fetchSiteWlansThunk(siteId))
    } catch (error) {
      console.log('Error while updating WLAN', error)
    }
  }
}

export const enableWLANThunk = (siteId: string, body: EnableWLANBody) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: createWLANActions,
        asyncFunc: () => enableWLAN(siteId, body),
      })

      await dispatch(fetchSiteWlansThunk(siteId))
    } catch (error) {
      console.log('Error while updating WLAN', error)
    }
  }
}

export const deleteWLANThunk = (siteId: string, wlanId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: createWLANActions,
        asyncFunc: () => deleteWLAN(siteId, wlanId),
      })

      await dispatch(fetchSiteWlansThunk(siteId))
    } catch (error) {
      console.log('Error while deleting WLAN', error)
    }
  }
}

const orgTemplatesSlice = makeFetchSlice(ORG_TEMPLATES_REDUCER_NAME)

export const orgTemplatesReducer = orgTemplatesSlice.reducer
export const orgTemplatesActions = orgTemplatesSlice.actions

export const fetchOrgTemplatesThunk = (orgId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: orgTemplatesActions,
        asyncFunc: () => fetchOrgTemplates(orgId),
      })
    } catch (error) {
      console.log('Error fetching Templates', error)
    }
  }
}

export const createOrgTemplateThunk = (orgId: string, body: OrgTemplates) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: orgTemplatesActions,
        asyncFunc: () => createOrgTemplate(orgId, body),
      })

      await dispatch(fetchOrgTemplatesThunk(orgId))
    } catch (error) {
      console.log('Error while creating template', error)
      throw error
    }
  }
}

export const deleteOrgTemplatesThunk = (
  orgId: string,
  body: DeleteOrgTemplate
) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: orgTemplatesActions,
        asyncFunc: () => deleteOrgTemplate(orgId, body),
      })
      await dispatch(fetchOrgTemplatesThunk(orgId))
    } catch (error) {
      console.log('Error deleting Templates', error)
    }
  }
}

const inventorySlice = makeFetchSlice(INVENTORY_REDUCER_NAME)

export const inventoryReducer = inventorySlice.reducer
export const inventoryActions = inventorySlice.actions

export const fetchInventoryThunk = (siteId: string) => {
  return async (dispatch: AppDispatch) => {
    if (siteId) {
      try {
        await fetchDataThunk({
          dispatch,
          actions: inventoryActions,
          asyncFunc: () => fetchInventory(siteId),
        })
      } catch (error) {
        console.log('Error fetching Inventory', error)
      }
    }
  }
}

export const deleteClaimDeviceThunk = (
  orgId: string,
  siteId: string,
  mac: string
) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: siteDeviceStatsActions,
        asyncFunc: () => deleteClaimDevice(orgId, mac, mac),
      })
      await dispatch(fetchInventoryThunk(siteId))
    } catch (error) {
      console.log('Error Delete Claim Device', error)
    }
  }
}

export const deleteSiteClaimDeviceThunk = (
  orgId: string,
  mac: string,
  magic: string,
  siteId: string
) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: siteDeviceStatsActions,
        asyncFunc: () => deleteClaimDevice(orgId, mac, magic),
      })
      await dispatch(fetchSiteDeviceStatsThunk(siteId))
    } catch (error) {
      console.log('Error Delete Claim Device', error)
    }
  }
}

const orgSiteWlansSlice = makeFetchSlice(ORG_SITE_WLANS_REDUCER_NAME)

export const orgSiteWlansReducer = orgSiteWlansSlice.reducer
export const orgSiteWlansActions = orgSiteWlansSlice.actions

export const fetchOrgSiteWlansThunk = (orgId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: orgSiteWlansActions,
        asyncFunc: () => fetchOrgSiteWlans(orgId),
      })
    } catch (error) {
      console.log('Error fetching Templates', error)
    }
  }
}

const sitePSKsSlice = makeFetchSlice(SITE_PSKS_REDUCER_NAME)

export const sitePSKsReducer = sitePSKsSlice.reducer
export const sitePSKsActions = sitePSKsSlice.actions

export const fetchSitePSKsThunk = (siteId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: sitePSKsActions,
        asyncFunc: () => fetchSitePSKs(siteId),
      })
    } catch (error) {
      console.log('Error fetching PSKs', error)
    }
  }
}

export const createSitePSKsThunk = (siteId: string, body: SitePSKs) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        asyncFunc: () => createSitePSKs(siteId, body),
      })
    } catch (error) {
      console.log('Error while creating Site PSKs', error)
    }
  }
}

export const updateSitePSKsThunk = (
  siteId: string,
  pskId: string,
  body: UpdateSitePSKs
) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        asyncFunc: () => updateSitePSKs(siteId, pskId, body),
      })
    } catch (error) {
      console.log('Error while updating Site PSKs', error)
    }
  }
}

export const updateSiteMultiPSKsThunk = (
  siteId: string,
  body: [UpdateSitePSKs]
) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: sitePSKsActions,
        asyncFunc: () => updateSiteMultiPSKs(siteId, body),
      })

      await dispatch(fetchSitePSKsThunk(siteId))
    } catch (error) {
      console.log('Error while updating Site Multi PSKs', error)
    }
  }
}

export const deleteSitePSKsThunk = (siteId: string, pskId: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        asyncFunc: () => deleteSitePSKs(siteId, pskId),
      })
      await dispatch(fetchSitePSKsThunk(siteId))
    } catch (error) {
      console.log('Error Delete Site PSKs', error)
    }
  }
}

export const deleteSiteSsidPSKsThunk = (siteId: string, ssid: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        asyncFunc: () => deleteSsidSitePSKs(siteId, ssid),
      })
      await dispatch(fetchSitePSKsThunk(siteId))
    } catch (error) {
      console.log('Error Delete Site PSKs', error)
    }
  }
}

const orgRFTemplateSlice = makeFetchSlice(ORG_RF_TEMPLATES_REDUCER_NAME)

export const orgRFTemplateReducer = orgRFTemplateSlice.reducer
export const orgRFTemplateActions = orgRFTemplateSlice.actions

export const fetchOrgRFTemplateThunk = (
  orgId: string,
  rfTemplateId: string
) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: orgRFTemplateActions,
        asyncFunc: () => fetchOrgSiteRFTemplate(orgId, rfTemplateId),
      })
    } catch (error) {
      console.log('Error fetching Org RF Templates', error)
    }
  }
}

export const updateOrgRFTemplateThunk = (
  orgId: string,
  rfTemplateId: string,
  body: UpdateOrgRFTemplate
) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        actions: orgRFTemplateActions,
        asyncFunc: () => updateOrgRFTemplate(orgId, rfTemplateId, body),
      })

      await dispatch(fetchOrgRFTemplateThunk(orgId, rfTemplateId))
    } catch (error) {
      console.log('Error while updating RT Template', error)
    }
  }
}

export const optimizeRadiosThunk = (siteId: string, body: OptimizeRadios) => {
  return async (dispatch: AppDispatch) => {
    try {
      await fetchDataThunk({
        dispatch,
        asyncFunc: () => optimizeRadios(siteId, body),
      })
    } catch (error) {
      console.log('Error optimizing radios', error)
    }
  }
}
